import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Form, message, Modal, notification, Select } from 'antd';
import { RootState } from '../../../../rootReducer';
import { connect } from 'react-redux';
import {
  getFindingTypeProperties,
  moveToNextPage,
  moveToPreviousPage,
  TypeRepresentations,
  FindingTypeDetails,
  populateFindingsType,
  removeSelectedType,
  FindingTypeTemplateDetails,
  updateSelectedFindingTypeTemplates
} from '../AnnotationStandardForm.slice';
import { AppDispatch } from '../../../../store';
import Actions from '../Actions/Actions';
import SelectedFindingType from './SelectedFindingType';
import rootStyles from '../AnnotationStandardForm.module.scss';
import { ToastAnt } from '../../../Shared/Toast/ToastAnt';
import FindingTypesOverview from './FindingTypesOverview';
import styles from './SelectedFindingType.module.scss';
import { ExclamationCircleOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { FailureMechanismTemplate } from '../../FailureMechanismTemplate/failureMechanismTemplate.slice';
import { FailureModeCategoryTemplate } from '../../FailureModeCategoryTemplate/failureModeCategoryTemplate.slice';
import { EightDTemplate } from '../../EightDTemplate/eightDTemplate.slice';

const { Option } = Select;
const { confirm } = Modal;

type Props = {
  findingTypeTemplates: Array<FindingTypeTemplateDetails>;
  selectedFindingTypes: Array<FindingTypeTemplateDetails>;
  populatedFindingTypes: Array<FindingTypeDetails>;
  canEdit: boolean;
  failureMechanismTemplates: Array<FailureMechanismTemplate>;
  failureModeCategoryTemplates: Array<FailureModeCategoryTemplate>;
  eightDTemplates: Array<EightDTemplate>;
  onFindingTypeSelected: (id: string) => void;
  onMoveToNextPage: () => void;
  onMoveToPreviousPage: () => void;
  onPopulateFindingType: (findingTypes: FindingTypeDetails[]) => void;
  onRemoveSelectedType: (id: string) => void;
  onUpdateSelectedFindingTypeTemplates: (selectedTemplates: FindingTypeTemplateDetails[]) => void;
};

const AddFindingTypes: FC<Props> = ({
  findingTypeTemplates,
  selectedFindingTypes,
  populatedFindingTypes,
  canEdit,
  failureMechanismTemplates,
  failureModeCategoryTemplates,
  eightDTemplates,
  onFindingTypeSelected,
  onMoveToNextPage,
  onMoveToPreviousPage,
  onPopulateFindingType,
  onRemoveSelectedType,
  onUpdateSelectedFindingTypeTemplates
}) => {
  const [findingTypes, setFindingTypes] = useState<Array<FindingTypeDetails>>(populatedFindingTypes);
  const [showOverview, setShowOverview] = useState(false);

  const onChange = useCallback(
    (value: string, showMessageForDuplicate = true) => {
      if (findingTypes.find(x => x.findingTypeTemplateId === value) && showMessageForDuplicate) {
        ToastAnt(`Selected finding type has been already added.`);
        return;
      }

      onFindingTypeSelected(value);
    },
    [findingTypes, onFindingTypeSelected]
  );

  const onNextPage = () => {
    onPopulateFindingType(findingTypes);
    onMoveToNextPage();
  };

  const onPreviousPage = () => {
    onMoveToPreviousPage();
  };

  const isValid = () => selectedFindingTypes.length > 0 && findingTypes.every(x => x.representations.every(x => x.title !== ''));

  const onPropertiesChanged = (findingTypeTemplateId: string, representations: TypeRepresentations) => {
    const findingTypeTemplate = findingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
    let newFindingTypes: FindingTypeDetails[];

    if (findingTypeTemplate) {
      newFindingTypes = findingTypes.map(x => {
        if (x.findingTypeTemplateId === findingTypeTemplateId) {
          return {
            ...x,
            representations: representations.representations
          };
        }

        return x;
      });
    } else {
      const findingType = findingTypeTemplates.find(item => item.id === findingTypeTemplateId);
      newFindingTypes = [
        ...findingTypes,
        {
          findingTypeTemplateId,
          representations: representations.representations,
          rules: [],
          order: findingTypes.length,
          findingTypeTemplate: findingType,
          failureMechanismTemplateIds: [],
          failureMechanisms: [],
          failureModeCategoryTemplateIds: [],
          failureModeCategories: [],
          eightDTemplateIds: [],
          eightDs: []
        }
      ];
    }
    setFindingTypes(newFindingTypes);
  };

  const onFailureMechanismChanged = (findingTypeTemplateId: string, selectedIds: string[]) => {
    const findingTypeTemplate = findingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
    let newFindingTypes: FindingTypeDetails[];

    if (findingTypeTemplate) {
      newFindingTypes = findingTypes.map(x => {
        if (x.findingTypeTemplateId === findingTypeTemplateId) {
          return {
            ...x,
            failureMechanismTemplateIds: selectedIds
          };
        }

        return x;
      });
    } else {
      const findingType = findingTypeTemplates.find(item => item.id === findingTypeTemplateId);
      newFindingTypes = [
        ...findingTypes,
        {
          findingTypeTemplateId,
          representations: [],
          rules: [],
          order: findingTypes.length,
          findingTypeTemplate: findingType,
          failureMechanismTemplateIds: selectedIds,
          failureMechanisms: [],
          failureModeCategoryTemplateIds: [],
          failureModeCategories: [],
          eightDTemplateIds: [],
          eightDs: []
        }
      ];
    }
    setFindingTypes(newFindingTypes);
  };

  const onFailureModeCategoryChanged = (findingTypeTemplateId: string, selectedIds: string[]) => {
    const findingTypeTemplate = findingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
    let newFindingTypes: FindingTypeDetails[];

    if (findingTypeTemplate) {
      newFindingTypes = findingTypes.map(x => {
        if (x.findingTypeTemplateId === findingTypeTemplateId) {
          return {
            ...x,
            failureModeCategoryTemplateIds: selectedIds
          };
        }

        return x;
      });
    } else {
      const findingType = findingTypeTemplates.find(item => item.id === findingTypeTemplateId);
      newFindingTypes = [
        ...findingTypes,
        {
          findingTypeTemplateId,
          representations: [],
          rules: [],
          order: findingTypes.length,
          findingTypeTemplate: findingType,
          failureMechanismTemplateIds: [],
          failureMechanisms: [],
          failureModeCategoryTemplateIds: selectedIds,
          failureModeCategories: [],
          eightDTemplateIds: [],
          eightDs: []
        }
      ];
    }
    setFindingTypes(newFindingTypes);
  };

  const onEightDChanged = (findingTypeTemplateId: string, selectedIds: string[]) => {
    const findingTypeTemplate = findingTypes.find(x => x.findingTypeTemplateId === findingTypeTemplateId)!;
    let newFindingTypes: FindingTypeDetails[];

    if (findingTypeTemplate) {
      newFindingTypes = findingTypes.map(x => {
        if (x.findingTypeTemplateId === findingTypeTemplateId) {
          return {
            ...x,
            eightDTemplateIds: selectedIds
          };
        }

        return x;
      });
    } else {
      const findingType = findingTypeTemplates.find(item => item.id === findingTypeTemplateId);
      newFindingTypes = [
        ...findingTypes,
        {
          findingTypeTemplateId,
          representations: [],
          rules: [],
          order: findingTypes.length,
          findingTypeTemplate: findingType,
          failureMechanismTemplateIds: [],
          failureMechanisms: [],
          failureModeCategoryTemplateIds: [],
          failureModeCategories: [],
          eightDTemplateIds: selectedIds,
          eightDs: []
        }
      ];
    }
    setFindingTypes(newFindingTypes);
  };

  const removeSelectedFinding = (id: string) => {
    onRemoveSelectedType(id);
    setFindingTypes(
      findingTypes
        .filter(x => x.findingTypeTemplateId !== id)
        .sort((a, b) => a.order - b.order)
        .map((x, index) => ({
          ...x,
          order: index
        }))
    );
  };

  const generateTypes = () => {
    return (
      <div>
        {selectedFindingTypes.map((findingType, index) => (
          <SelectedFindingType
            key={findingType.id}
            onPropertiesChanged={onPropertiesChanged}
            onRemoveType={removeSelectedFinding}
            findingType={findingType}
            marginTop={index !== 0 ? '2rem' : 0}
            createdFindingType={findingTypes.find(x => x.findingTypeTemplateId === findingType.id)}
            canEdit={canEdit}
            failureMechanismTemplates={failureMechanismTemplates}
            failureModeCategoryTemplates={failureModeCategoryTemplates}
            eightDTemplates={eightDTemplates}
            onFailureMechanismChanged={onFailureMechanismChanged}
            onFailureModeCategoryChanged={onFailureModeCategoryChanged}
            onEightDChanged={onEightDChanged}
          />
        ))}
      </div>
    );
  };

  const loadMLFindingTypes = useCallback(() => {
    notification.close('load-ml-ft');

    const mlFindingTypes = findingTypeTemplates.filter(x => x.isMLRelated).filter(x => !selectedFindingTypes.map(y => y.id).includes(x.id));
    mlFindingTypes.forEach(x => onChange(x.id, false));
  }, [findingTypeTemplates, onChange, selectedFindingTypes]);

  useEffect(() => {
    if (findingTypeTemplates.filter(x => x.isMLRelated).some(x => !selectedFindingTypes.map(y => y.id).includes(x.id))) {
      confirm({
        title: 'AI finding types',
        content: 'Do you want to add missing AI finding types?',
        icon: <ExclamationCircleOutlined />,
        onOk() {
          loadMLFindingTypes();
        }
      });
    }
  }, []);

  useEffect(() => {
    if (selectedFindingTypes.length === 0) {
      const unknownFT = findingTypeTemplates.find(x => x.name.toLowerCase() === 'unknown');
      if (unknownFT) {
        onChange(unknownFT.id);
      }
    }
  }, [findingTypeTemplates, onChange, selectedFindingTypes.length]);

  return (
    <>
      <div>
        <div className={styles.ftActions}>
          <Button type='primary' onClick={() => setShowOverview(true)}>
            <UnorderedListOutlined />
          </Button>
          <Form.Item label='Add Finding type:'>
            <Select
              disabled={!canEdit}
              showSearch
              style={{ width: 200 }}
              placeholder='Please select'
              optionFilterProp='children'
              onChange={onChange}
              filterOption={true}
            >
              {findingTypeTemplates
                .filter(x => !x.isMLRelated)
                .map(type => (
                  <Option value={type.id} key={type.id}>
                    {type.name} {type.version}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>
        <div className={rootStyles.contentContainer}>
          <div>{generateTypes()}</div>
        </div>
        <Actions isLoading={false} onNext={onNextPage} onPrevious={onPreviousPage} isDisabled={!isValid()} />
      </div>
      <FindingTypesOverview
        showModal={showOverview}
        closeModal={() => setShowOverview(false)}
        findingTypes={findingTypes}
        setFindingTypes={setFindingTypes}
        selectedFindingTypes={selectedFindingTypes}
        onUpdateSelectedFindingTypeTemplates={onUpdateSelectedFindingTypeTemplates}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  findingTypeTemplates: state.annotationStandardForm.findingTypeTemplates,
  failureMechanismTemplates: state.annotationStandardForm.failureMechanismTemplates,
  failureModeCategoryTemplates: state.annotationStandardForm.failureModeCategoryTemplates,
  eightDTemplates: state.annotationStandardForm.eightDTemplates,
  selectedFindingTypes: state.annotationStandardForm.selectedFindingTypeTemplates,
  populatedFindingTypes: state.annotationStandardForm.annotationStandard.findingTypes
    ? state.annotationStandardForm.annotationStandard.findingTypes
    : [],
  canEdit: state.annotationStandardForm.canEdit
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onFindingTypeSelected: (id: string) => dispatch(getFindingTypeProperties(id)),
  onRemoveSelectedType: (id: string) => dispatch(removeSelectedType(id)),
  onMoveToNextPage: () => dispatch(moveToNextPage()),
  onMoveToPreviousPage: () => dispatch(moveToPreviousPage()),
  onPopulateFindingType: (findingTypes: FindingTypeDetails[]) => dispatch(populateFindingsType(findingTypes)),
  onUpdateSelectedFindingTypeTemplates: (selectedTemplates: FindingTypeTemplateDetails[]) =>
    dispatch(updateSelectedFindingTypeTemplates(selectedTemplates))
});

export default connect(mapStateToProps, mapDispatchToProps)(AddFindingTypes);
